/*
  The one place that talks to Cytoscape.js. The library is loaded only when a navigator opens (dynamic import),
  so the rest of the app does not carry it. Only built-in features are used: the "preset" layout (the positions
  come from `layout.ts`), tap and double-tap, zoom and pan. In a test the graph is made without a page
  (`headless`), because jsdom has no canvas.
*/

import type cytoscape from "cytoscape";

import type { Id } from "../../../api/types";
import { buildStyle, type Palette } from "./elements";

export interface GraphHandlers {
  /** A node was tapped, or the empty background (null). */
  onSelect(id: Id | null): void;
  /** A node was tapped twice: open or fold it. */
  onToggle(id: Id): void;
}

export interface GraphOptions extends GraphHandlers {
  container: HTMLElement | null;
  palette: Palette;
  headless?: boolean;
}

export interface GraphHandle {
  /** Show these elements. Elements that are no longer in the list go. The first time, the picture is set to a readable view. */
  update(elements: cytoscape.ElementDefinition[]): void;
  setPalette(palette: Palette): void;
  fit(): void;
  zoomBy(factor: number): void;
  /** Bring a node to the middle of the picture. */
  centerOn(id: Id, animate: boolean): void;
  nodeCount(): number;
  destroy(): void;
}

type Factory = typeof cytoscape;

/**
 * Load Cytoscape and make a graph. `aborted` is asked once the library has arrived: a screen that was closed
 * in the meantime (or opened twice in a row, as React does while developing) gets no graph and no leftovers.
 */
export async function mountGraph(options: GraphOptions, aborted: () => boolean = () => false): Promise<GraphHandle | null> {
  const module = await import("cytoscape");
  if (aborted()) return null;
  return createGraph(module.default as Factory, options);
}

export function createGraph(factory: Factory, options: GraphOptions): GraphHandle {
  const cy = factory({
    container: options.headless ? undefined : (options.container ?? undefined),
    headless: options.headless === true,
    styleEnabled: true,
    elements: [],
    style: buildStyle(options.palette),
    layout: { name: "preset" },
    minZoom: 0.12,
    maxZoom: 2.5,
    boxSelectionEnabled: false,
    autounselectify: true,
    autoungrabify: true,
  });

  cy.on("tap", "node", (event) => options.onSelect(String(event.target.id())));
  cy.on("dbltap", "node", (event) => options.onToggle(String(event.target.id())));
  cy.on("tap", (event) => {
    if (event.target === cy) options.onSelect(null);
  });

  let first = true;
  let destroyed = false;
  const sized = () => !options.headless && cy.width() > 0 && cy.height() > 0;

  /**
   * The first view: wide enough to read the names, from the top left, instead of the whole tree squeezed into the
   * box (a tree of a few hundred items fitted whole is too small to read). "Fit to screen" gives the whole tree.
   */
  const readableView = () => {
    const box = cy.elements().boundingBox({});
    if (box.w <= 0) return;
    const pad = 28;
    const zoom = Math.max(0.5, Math.min(1, (cy.width() - pad * 2) / box.w));
    cy.zoom(zoom);
    cy.pan({ x: pad - box.x1 * zoom, y: pad - box.y1 * zoom });
  };

  return {
    update(elements) {
      if (destroyed) return;
      cy.json({ elements });
      if (first && elements.length > 0 && sized()) {
        first = false;
        readableView();
      }
    },
    setPalette(palette) {
      if (!destroyed) cy.style(buildStyle(palette));
    },
    fit() {
      if (!destroyed && sized()) cy.fit(undefined, 36);
    },
    zoomBy(factor) {
      if (destroyed || !sized()) return;
      cy.zoom({ level: cy.zoom() * factor, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } });
    },
    centerOn(id, animate) {
      if (destroyed || !sized()) return;
      const node = cy.getElementById(id);
      if (node.empty()) return;
      if (animate) cy.animate({ center: { eles: node }, duration: 180 });
      else cy.center(node);
    },
    nodeCount() {
      return destroyed ? 0 : cy.nodes().length;
    },
    destroy() {
      destroyed = true;
      cy.destroy();
    },
  };
}
